<template>
    <div class="sp-header-wrapper">
        <div class="d-flex justify-content-start align-items-center dashboard-title-wrap">
            <a @click="goBack()" class="btn-back cursor-pointer">
                <img src="~@/assets/images/savings-back.png" alt="" />
            </a>
            <h2>SP Data Management</h2>
        </div>

        <Toolbar class="p-tool">
            <template #start>
                <div class="start-controls">
                    <FloatLabel variant="on" v-if="isDesktop && localSelectedColumns" class="column-selector">
                        <MultiSelect id="sp_columns" :modelValue="localSelectedColumns" :options="allColumns"
                            optionLabel="header" filter :maxSelectedLabels="3" class="multi w-full md:w-80" display="chip"
                            placeholder="Select Columns" size="small" inputId="sp-columns"
                            @update:modelValue="updateColumns" />
                        <label for="sp-columns" v-if="localSelectedColumns.length !== 0">Columns</label>
                    </FloatLabel>
                    <FloatLabel variant="on" v-if="filters && filters['global']" class="search-field">
                        <IconField class="search-container" id="sp_management_search">
                            <InputIcon><i class="pi pi-search" /></InputIcon>
                            <InputText
                                :modelValue="searchValue"
                                @update:modelValue="onSearchInput"
                                @paste="onSearchPaste"
                            />
                        </IconField>
                        <label for="sp_management_search" style="font-size:12px">Quick Search</label>
                    </FloatLabel>
                </div>
            </template>

            <template #end>
                <div class="button-group">
                    <Button type="button" v-tooltip.top="'Reset Filter'" icon="pi pi-filter-slash" outlined
                        @click="$emit('clear-filter')" size="small" class="action-button" />
                    <Button icon="pi pi-download" v-tooltip.top="'Download RM/SP Data'" outlined
                        @click="$emit('open-download')" size="small" class="action-button" />
                    <Button type="button" icon="pi pi-upload" v-tooltip.top="'Upload RM/SP Data'" outlined
                        @click="$emit('open-upload')" size="small" class="action-button" />
                </div>
            </template>
        </Toolbar>
    </div>
</template>
<script>
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import FloatLabel from 'primevue/floatlabel';
import IconField from 'primevue/iconfield';
import InputText from 'primevue/inputtext';
import InputIcon from 'primevue/inputicon';
import MultiSelect from 'primevue/multiselect';
import { stripHtml } from '@/utils/inputSanitizer';

export default {
    name: 'SPManagementHeader',
    components: {
        Toolbar,
        Button,
        FloatLabel,
        IconField,
        InputText,
        InputIcon,
        MultiSelect
    },
    props: {
        filters: {
            type: Object,
            required: true
        },
        allColumns: {
            type: Array,
            required: true
        },
        selectedColumns: {
            type: Array,
            required: true
        },
        isDesktop: {
            type: Boolean,
            default: true
        },
        distributorID: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: ['update-search', 'update-columns', 'clear-filter', 'export-csv', 'open-download', 'open-upload'],
    data() {
        return {
            searchValue: this.filters?.global?.value || '',
            localSelectedColumns: [...this.selectedColumns]
        };
    },
    watch: {
        'filters.global.value': {
            handler(newVal) {
                if (newVal !== this.searchValue) {
                    this.searchValue = newVal || '';
                }
            },
            immediate: true
        },
        selectedColumns: {
            handler(newVal) {
                this.localSelectedColumns = [...newVal];
            },
            deep: true
        },
        searchValue(newVal) {
            this.$emit('update-search', newVal);
        }
    },
    methods: {
        updateColumns(value) {
            this.localSelectedColumns = value;
            this.$emit('update-columns', value);
        },
        goBack() {
            this.$router.push('/dashboard');
        },
        onSearchInput(value) {
            this.searchValue = stripHtml(value ?? '');
        },
        onSearchPaste(event) {
            event.preventDefault();
            const pasted = (event.clipboardData || window.clipboardData).getData('text');
            this.searchValue = stripHtml((this.searchValue ?? '') + pasted);
        },
    }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.sp-header-wrapper {
    width: 100%;
}

.dashboard-title-wrap {
    margin-bottom: 1rem;

    h2 {
        font-size: 1.125rem;
        font-weight: 400;
        margin: 0;
    }

    .btn-back {
        margin-right: 1rem;
        display: inline-flex;
        align-items: center;
        cursor: pointer;

        img {
            width: 24px;
            height: 24px;
        }
    }
}

// Toolbar styles
.p-tool {
    width: 100%;
}

// Start controls (Column selector and Search)
.start-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    width: 100%;
}

.column-selector {
    min-width: 200px;
}

.search-field {
    min-width: 200px;
    flex: 1;
}

.search-container {
    width: 100%;
}

// Button group (Action buttons)
.button-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: nowrap;
}

.action-button {
    flex-shrink: 0;
}

// Mobile responsive adjustments
@media (max-width: 767px) {
    .sp-header-wrapper {
        padding: 0;
    }

    .dashboard-title-wrap {
        margin-bottom: 1rem;

        h2 {
            font-size: 1rem;
            font-weight: 600;
        }

        .btn-back {
            margin-right: 0.75rem;

            img {
                width: 20px;
                height: 20px;
            }
        }
    }

    // Stack column selector and search vertically on mobile
    .start-controls {
        flex-direction: row;
        align-items: stretch;
        gap: 0.75rem;
        width: 100%;
        .p-multiselect, .p-inputtext {
            width: 100%;
            min-width: 100%;
            max-width: 100%;
        }
    }

    .column-selector,
    .search-field {
        width: 100%;
        min-width: 100%;
    }

    // Keep buttons in one line on mobile
    .button-group {
        width: 100%;
        justify-content: flex-start;
        gap: 0.5rem;
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .action-button {
        flex: 0 0 auto;
        min-width: 44px; // Touch-friendly size
        min-height: 44px;
    }

    // Adjust toolbar padding on mobile
    .p-tool {
        padding: 0.5rem;
    }
}

// Very small screens
@media (max-width: 480px) {
    .dashboard-title-wrap {
        h2 {
            font-size: 0.9rem;
        }
    }

    .button-group {
        gap: 0.25rem;
    }

    .action-button {
        padding: 0.5rem;
    }
}
</style>

